import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useSEO } from '../../../utils/seo';

const BlogPostPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  useSEO({
    title: 'Navigating International Customs: A Complete Guide | Go Global Inc.',
    description: 'Everything you need to know about customs clearance, documentation, and compliance when importing construction materials from overseas suppliers.',
    keywords: 'international customs, customs clearance, import documentation, compliance, freight forwarding',
    canonical: `${siteUrl}/blog/navigating-international-customs`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: 'Navigating International Customs: A Complete Guide for Contractors',
      description: 'Everything you need to know about customs clearance, documentation, and compliance when importing construction materials from overseas suppliers.',
      author: {
        '@type': 'Person',
        name: 'David Thompson',
      },
      datePublished: '2025-01-10',
      dateModified: '2025-01-10',
      publisher: {
        '@type': 'Organization',
        name: 'Go Global Inc.',
        logo: {
          '@type': 'ImageObject',
          url: `${siteUrl}/favicon.ico`,
        },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${siteUrl}/blog/navigating-international-customs`,
      },
    },
  });

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <main>
        <section className="pt-32 pb-12 lg:pt-40 lg:pb-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <Link to="/blog" className="inline-flex items-center text-primary-600 font-medium hover:text-primary-700 transition-colors mb-8 cursor-pointer">
              <i className="ri-arrow-left-line mr-2"></i>
              Back to Blog
            </Link>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-xs font-semibold text-primary-600 uppercase tracking-wide mb-4">
                Logistics
              </div>
              <h1 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl text-gray-900 leading-tight mb-6">
                Navigating International Customs: A Complete Guide for Contractors
              </h1>
              <div className="flex items-center space-x-4 text-gray-600 mb-8">
                <span>David Thompson</span>
                <span>•</span>
                <span>January 10, 2025</span>
                <span>•</span>
                <span>10 min read</span>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="pb-16">
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <div className="relative h-96 lg:h-[600px] rounded-2xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/162af49a99d9ec5d75b45644ea94da2b.jpg"
                alt="International Customs and Logistics"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </section>

        <section className="pb-20 lg:pb-28">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <article className="prose prose-lg max-w-none">
              <p className="text-xl text-gray-700 leading-relaxed mb-8">
                Importing construction materials from overseas can offer substantial cost savings, but navigating the complexities of international customs requires careful planning and attention to detail. This comprehensive guide will help you understand the essential requirements and avoid common pitfalls.
              </p>

              <h2 className="font-display font-semibold text-3xl text-gray-900 mt-12 mb-6">
                Understanding the Customs Process
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                The customs clearance process involves several key stages, from initial documentation preparation through final delivery. Understanding each step helps prevent delays and additional costs. When your shipment arrives at the destination port, customs authorities will review your documentation, assess duties and taxes, and inspect cargo as necessary.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                The entire process typically takes 2-7 business days for standard shipments, though complex cargo or incomplete documentation can extend this timeline significantly. Working with experienced customs brokers familiar with construction materials can streamline the process considerably.
              </p>

              <h2 className="font-display font-semibold text-3xl text-gray-900 mt-12 mb-6">
                Essential Documentation Requirements
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Proper documentation is crucial for smooth customs clearance. At minimum, you'll need a commercial invoice detailing the transaction value, a packing list describing contents and weights, and a bill of lading serving as proof of shipment. Additional documents may include certificates of origin, product specifications, and safety compliance certifications.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Construction materials often require specific certifications demonstrating compliance with local building codes and safety standards. Research these requirements early in the procurement process to avoid surprises when your shipment arrives. Many manufacturers are familiar with export documentation requirements and can provide necessary certificates.
              </p>

              <h2 className="font-display font-semibold text-3xl text-gray-900 mt-12 mb-6">
                Harmonized System Codes and Classification
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Every product crossing international borders must be classified using the Harmonized System (HS) code, a standardized numerical method of categorizing traded products. Accurate classification is critical because it determines applicable duty rates, import restrictions, and regulatory requirements.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Misclassification can result in penalties, delays, and unexpected costs. When in doubt, consult with a customs broker or directly with customs authorities before shipping. Many countries provide online databases where you can research appropriate codes for your specific materials.
              </p>

              <h2 className="font-display font-semibold text-3xl text-gray-900 mt-12 mb-6">
                Calculating Duties, Taxes, and Fees
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Import costs extend beyond the purchase price of materials. Customs duties typically range from 0-15% depending on product classification and country of origin. Value-added tax (VAT) or goods and services tax (GST) may also apply, often calculated on the combined value of goods plus duties plus freight costs.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Additional fees can include harbor maintenance fees, merchandise processing fees, and customs broker charges. Request a detailed cost breakdown from your freight forwarder before committing to a shipment, ensuring you understand the true landed cost of your materials.
              </p>

              <h2 className="font-display font-semibold text-3xl text-gray-900 mt-12 mb-6">
                Common Pitfalls and How to Avoid Them
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                The most frequent customs issues arise from incomplete or inaccurate documentation. Ensure all commercial invoices clearly describe products using both common names and technical specifications. Verify that quantities, weights, and values match across all documents. Inconsistencies trigger additional scrutiny and delays.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Another common mistake is undervaluing goods to reduce duty payments. Customs authorities are sophisticated in detecting undervaluation, and the penalties far exceed any potential savings. Always declare the true transaction value and maintain records to support your valuation if questioned.
              </p>

              <h2 className="font-display font-semibold text-3xl text-gray-900 mt-12 mb-6">
                The Value of Professional Customs Brokers
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                While it's possible to handle customs clearance independently, the complexity and consequences of mistakes make professional assistance worthwhile for most contractors. Licensed customs brokers possess deep knowledge of regulations, maintain relationships with customs officials, and can often resolve issues that would take importers days or weeks to address.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Broker fees typically range from $100-500 per shipment depending on complexity, a modest investment considering the potential costs of delays or non-compliance. Look for brokers with specific experience in construction materials and positive references from other contractors.
              </p>

              <h2 className="font-display font-semibold text-3xl text-gray-900 mt-12 mb-6">
                Planning for Success
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Successful international procurement requires building customs considerations into your project timeline from the outset. Allow extra time for potential delays and maintain open communication with all parties. Establish relationships with reliable freight forwarders and customs brokers before you need them urgently.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                As you gain experience with international imports, the process becomes increasingly smooth and predictable. The contractors who invest time in understanding customs requirements and building strong logistics partnerships consistently achieve the best results in terms of cost, quality, and delivery reliability.
              </p>
            </article>

            <div className="mt-16 pt-8 border-t border-gray-200">
              <div className="flex items-start space-x-4">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-600 to-secondary-600 flex items-center justify-center text-white text-2xl font-semibold">
                  DT
                </div>
                <div>
                  <h3 className="font-display font-semibold text-xl text-gray-900 mb-2">David Thompson</h3>
                  <p className="text-gray-600 leading-relaxed">
                    David is a licensed customs broker with two decades of experience in international trade compliance. He specializes in helping construction companies navigate complex import regulations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPostPage;